import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Card } from '@/components/ui/card';

/**
 * Shimmer animation for skeleton loaders
 */
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

/**
 * Pulse animation for reduced motion
 */
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

/**
 * Base skeleton element with shimmer effect
 */
const SkeletonBase = styled.div<{ reduceMotion?: boolean }>`
  background: linear-gradient(
    90deg,
    #eef2f6 0px,
    #f7f9fc 40px,
    #eef2f6 80px
  );
  background-size: 1000px 100%;
  border-radius: 6px;
  animation: ${shimmer} 2s infinite linear;

  @media (prefers-reduced-motion: reduce) {
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

/**
 * Skeleton Metric Card
 */
const SkeletonMetricCardContainer = styled(Card)`
  border-radius: 8px;
  border: 1px solid #dee2e6;
  overflow: hidden;
`;

const SkeletonMetricHeader = styled.div`
  padding: 0.55rem 0.9rem 0.2rem 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SkeletonMetricBody = styled.div`
  padding: 0 0.9rem 0.55rem 0.9rem;
`;

const SkeletonLine = styled(SkeletonBase)<{ width?: string; height?: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  margin-bottom: 0.5rem;
`;

const SkeletonCircle = styled(SkeletonBase)<{ size?: string }>`
  width: ${props => props.size || '24px'};
  height: ${props => props.size || '24px'};
  border-radius: 50%;
`;

export function SkeletonMetricCard() {
  return (
    <SkeletonMetricCardContainer>
      <SkeletonMetricHeader>
        <SkeletonLine width="60%" height="14px" />
        <SkeletonCircle size="24px" />
      </SkeletonMetricHeader>
      <SkeletonMetricBody>
        <SkeletonLine width="80%" height="40px" />
        <SkeletonLine width="50%" height="14px" />
      </SkeletonMetricBody>
    </SkeletonMetricCardContainer>
  );
}

/**
 * Skeleton Inbox Item
 */
const SkeletonInboxContainer = styled(Card)`
  border-radius: 6px;
  border: 1px solid #dee2e6;
  margin-bottom: 0.5rem;
`;

const SkeletonInboxContent = styled.div`
  display: flex;
  align-items: start;
  gap: 0.75rem;
  padding: 1rem;
`;

const SkeletonInboxText = styled.div`
  flex: 1;
`;

export function SkeletonInboxItem() {
  return (
    <SkeletonInboxContainer>
      <SkeletonInboxContent>
        <SkeletonCircle size="20px" />
        <SkeletonInboxText>
          <SkeletonLine width="70%" height="14px" />
          <SkeletonLine width="90%" height="14px" />
          <SkeletonLine width="40%" height="12px" />
        </SkeletonInboxText>
      </SkeletonInboxContent>
    </SkeletonInboxContainer>
  );
}

/**
 * Skeleton Activity Item
 */
const SkeletonActivityContainer = styled(Card)`
  border-radius: 6px;
  border: 1px solid #dee2e6;
  margin-bottom: 0.5rem;
`;

const SkeletonActivityContent = styled.div`
  display: flex;
  align-items: start;
  gap: 0.75rem;
  padding: 0.75rem;
`;

const SkeletonActivityText = styled.div`
  flex: 1;
`;

export function SkeletonActivityItem() {
  return (
    <SkeletonActivityContainer>
      <SkeletonActivityContent>
        <SkeletonCircle size="20px" />
        <SkeletonActivityText>
          <SkeletonLine width="80%" height="14px" />
          <SkeletonLine width="60%" height="12px" />
          <SkeletonLine width="30%" height="12px" />
        </SkeletonActivityText>
      </SkeletonActivityContent>
    </SkeletonActivityContainer>
  );
}

/**
 * Full Dashboard Skeleton Loader
 */
const SkeletonDashboardContainer = styled.div`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: grid;
  grid-template-rows: 60px 1fr 64px;
  min-height: 0;
  width: 100%;
  margin: 0;

  @media (min-width: 768px) {
    grid-template-rows: 60px 1fr 64px;
  }
`;

const SkeletonHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  background: #ffffff;
`;

const SkeletonHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SkeletonContentArea = styled.div`
  padding: 1.25rem 1.5rem;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 1rem;
  min-height: 0;
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(34, 123, 126, 0.1), transparent 60%),
    radial-gradient(900px 500px at 90% 10%, rgba(59, 130, 246, 0.06), transparent 55%),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
`;

const SkeletonMetricGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SkeletonPanelsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 1rem;
  min-height: 0;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonSectionTitle = styled.div`
  margin-bottom: 1rem;
`;

const SkeletonPanelCard = styled(Card)`
  background: rgba(255, 255, 255, 0.68);
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 14px;
  box-shadow:
    0 16px 40px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  padding: 1rem 1.25rem;
  min-height: 0;
`;

const SkeletonCompactActivityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;

  @media (min-width: 1440px) {
    gap: 1.5rem;
  }
`;

const SkeletonActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(248, 250, 252, 0.72);
`;

const SkeletonActionPill = styled(SkeletonBase)`
  width: 140px;
  height: 30px;
  border-radius: 999px;
`;

export function DashboardSkeleton() {
  return (
    <SkeletonDashboardContainer>
      <SkeletonHeaderBar>
        <SkeletonLine width="140px" height="20px" />
        <SkeletonHeaderActions>
          <SkeletonLine width="120px" height="16px" />
          <SkeletonCircle size="26px" />
        </SkeletonHeaderActions>
      </SkeletonHeaderBar>

      <SkeletonContentArea>
        <SkeletonMetricGrid>
          <SkeletonMetricCard />
          <SkeletonMetricCard />
          <SkeletonMetricCard />
          <SkeletonMetricCard />
        </SkeletonMetricGrid>

        <SkeletonPanelsGrid>
          <SkeletonPanelCard>
            <SkeletonSectionTitle>
              <SkeletonLine width="120px" height="18px" />
            </SkeletonSectionTitle>
            <SkeletonInboxItem />
            <SkeletonInboxItem />
            <SkeletonInboxItem />
            <SkeletonInboxItem />
          </SkeletonPanelCard>
          <SkeletonPanelCard>
            <SkeletonCompactActivityGrid>
              <div>
                <SkeletonSectionTitle>
                  <SkeletonLine width="110px" height="16px" />
                </SkeletonSectionTitle>
                <SkeletonActivityItem />
                <SkeletonActivityItem />
              </div>
              <div>
                <SkeletonSectionTitle>
                  <SkeletonLine width="110px" height="16px" />
                </SkeletonSectionTitle>
                <SkeletonActivityItem />
                <SkeletonActivityItem />
              </div>
            </SkeletonCompactActivityGrid>
          </SkeletonPanelCard>
        </SkeletonPanelsGrid>
      </SkeletonContentArea>

      <SkeletonActionsBar>
        <SkeletonActionPill />
        <SkeletonActionPill />
        <SkeletonActionPill />
        <SkeletonActionPill />
      </SkeletonActionsBar>
    </SkeletonDashboardContainer>
  );
}

/**
 * New User Dashboard Skeleton
 */
const SkeletonOnboardingWelcome = styled.div`
  border-radius: 12px;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(34, 123, 126, 0.95) 0%, rgba(26, 95, 97, 0.95) 100%);
  color: white;
  margin-bottom: 2rem;
`;

const SkeletonOnboardingText = styled.div`
  display: grid;
  gap: 0.6rem;
  max-width: 520px;
`;

const SkeletonNewUserContainer = styled.div`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: grid;
  grid-template-rows: 60px 1fr 64px;
  min-height: 0;
  width: 100%;
`;

const SkeletonNewUserContent = styled.div`
  padding: 1.5rem;
  background: #ffffff;
`;

export function NewUserDashboardSkeleton() {
  return (
    <SkeletonNewUserContainer>
      <SkeletonHeaderBar>
        <SkeletonLine width="140px" height="20px" />
        <SkeletonHeaderActions>
          <SkeletonLine width="120px" height="16px" />
          <SkeletonCircle size="26px" />
        </SkeletonHeaderActions>
      </SkeletonHeaderBar>

      <SkeletonNewUserContent>
        <SkeletonOnboardingWelcome>
          <SkeletonOnboardingText>
            <SkeletonLine width="70%" height="20px" />
            <SkeletonLine width="90%" height="14px" />
          </SkeletonOnboardingText>
        </SkeletonOnboardingWelcome>
        <CTASkeleton />
      </SkeletonNewUserContent>

      <SkeletonActionsBar>
        <SkeletonActionPill />
        <SkeletonActionPill />
        <SkeletonActionPill />
      </SkeletonActionsBar>
    </SkeletonNewUserContainer>
  );
}

/**
 * Empty User Dashboard Skeleton
 */
const SkeletonEmptyContainer = styled.div`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: grid;
  grid-template-rows: 60px 1fr 64px;
  min-height: 0;
  width: 100%;
`;

const SkeletonEmptyContent = styled.div`
  padding: 1.5rem;
  background: #ffffff;
  display: grid;
  gap: 1.5rem;
`;

const SkeletonEmptyCard = styled(Card)`
  border-radius: 10px;
  border: 2px dashed #dee2e6;
  background: #f8f9fa;
  padding: 2.5rem 2rem;
  text-align: center;
`;

const SkeletonEmptyActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.25rem;
`;

const SkeletonEmptyButton = styled(SkeletonBase)`
  height: 36px;
  width: 180px;
  border-radius: 6px;
`;

export function EmptyUserDashboardSkeleton() {
  return (
    <SkeletonEmptyContainer>
      <SkeletonHeaderBar>
        <SkeletonLine width="140px" height="20px" />
        <SkeletonHeaderActions>
          <SkeletonLine width="120px" height="16px" />
          <SkeletonCircle size="26px" />
        </SkeletonHeaderActions>
      </SkeletonHeaderBar>

      <SkeletonEmptyContent>
        <SkeletonEmptyCard>
          <SkeletonCircle size="48px" />
          <div style={{ marginTop: '1rem' }}>
            <SkeletonLine width="45%" height="18px" />
            <SkeletonLine width="70%" height="14px" />
            <SkeletonLine width="60%" height="14px" />
          </div>
          <SkeletonEmptyActions>
            <SkeletonEmptyButton />
            <SkeletonEmptyButton />
          </SkeletonEmptyActions>
        </SkeletonEmptyCard>

        <SkeletonEmptyCard>
          <SkeletonCircle size="48px" />
          <div style={{ marginTop: '1rem' }}>
            <SkeletonLine width="45%" height="18px" />
            <SkeletonLine width="70%" height="14px" />
            <SkeletonLine width="60%" height="14px" />
          </div>
          <SkeletonEmptyActions>
            <SkeletonEmptyButton />
            <SkeletonEmptyButton />
          </SkeletonEmptyActions>
        </SkeletonEmptyCard>
      </SkeletonEmptyContent>

      <SkeletonActionsBar>
        <SkeletonActionPill />
        <SkeletonActionPill />
        <SkeletonActionPill />
        <SkeletonActionPill />
      </SkeletonActionsBar>
    </SkeletonEmptyContainer>
  );
}

/**
 * Established User Dashboard Skeleton
 */
const SkeletonEstablishedContainer = styled.div`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: grid;
  grid-template-columns: 350px 1fr;
  grid-template-rows: 60px 1fr 64px;
  min-height: 0;
  width: 100%;
  background: #ffffff;

  @media (min-width: 1440px) {
    grid-template-columns: 380px 1fr;
  }
`;

const SkeletonMetricSidebar = styled.div`
  grid-row: 2 / 3;
  border-right: 2px solid #dee2e6;
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
  display: grid;
  grid-template-rows: repeat(4, minmax(0, 1fr));
`;

const SkeletonMetricSidebarCard = styled(Card)`
  border-radius: 0;
  border: none;
  border-bottom: 1px solid #dee2e6;
  padding: 0.8rem 1rem;
`;

const SkeletonMetricSidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
`;

const SkeletonContentFeed = styled.div`
  grid-column: 2 / 3;
  grid-row: 2 / 3;
  padding: 1.5rem;
  overflow: hidden;
`;

const SkeletonFeedSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SkeletonFeedList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

export function EstablishedUserDashboardSkeleton() {
  return (
    <SkeletonEstablishedContainer>
      <SkeletonHeaderBar style={{ gridColumn: '1 / -1' }}>
        <SkeletonLine width="140px" height="20px" />
        <SkeletonHeaderActions>
          <SkeletonLine width="120px" height="16px" />
          <SkeletonCircle size="26px" />
        </SkeletonHeaderActions>
      </SkeletonHeaderBar>

      <SkeletonMetricSidebar>
        {[0, 1, 2, 3].map((index) => (
          <SkeletonMetricSidebarCard key={`metric-skel-${index}`}>
            <SkeletonMetricSidebarHeader>
              <SkeletonLine width="55%" height="12px" />
              <SkeletonCircle size="18px" />
            </SkeletonMetricSidebarHeader>
            <SkeletonLine width="65%" height="28px" />
            <SkeletonLine width="45%" height="12px" />
          </SkeletonMetricSidebarCard>
        ))}
      </SkeletonMetricSidebar>

      <SkeletonContentFeed>
        <SkeletonFeedSection>
          <SkeletonSectionTitle>
            <SkeletonLine width="140px" height="18px" />
          </SkeletonSectionTitle>
          <SkeletonFeedList>
            <SkeletonInboxItem />
            <SkeletonInboxItem />
            <SkeletonInboxItem />
          </SkeletonFeedList>
        </SkeletonFeedSection>

        <SkeletonFeedSection>
          <SkeletonSectionTitle>
            <SkeletonLine width="160px" height="18px" />
          </SkeletonSectionTitle>
          <SkeletonPanelsGrid>
            <div>
              <SkeletonSectionTitle>
                <SkeletonLine width="110px" height="16px" />
              </SkeletonSectionTitle>
              <SkeletonActivityItem />
              <SkeletonActivityItem />
            </div>
            <div>
              <SkeletonSectionTitle>
                <SkeletonLine width="110px" height="16px" />
              </SkeletonSectionTitle>
              <SkeletonActivityItem />
              <SkeletonActivityItem />
            </div>
          </SkeletonPanelsGrid>
        </SkeletonFeedSection>
      </SkeletonContentFeed>

      <SkeletonActionsBar style={{ gridColumn: '1 / -1' }}>
        <SkeletonActionPill />
        <SkeletonActionPill />
        <SkeletonActionPill />
        <SkeletonActionPill />
      </SkeletonActionsBar>
    </SkeletonEstablishedContainer>
  );
}

/**
 * Skeleton CTA Card
 */
const SkeletonCTACardContainer = styled(Card)`
  border-radius: 8px;
  border: 2px solid #e9ecef;
  overflow: hidden;
  position: relative;
`;

const SkeletonCTAContent = styled.div`
  padding: 1.25rem 2.5rem 1.25rem 1.25rem;
`;

const SkeletonCTAIconArea = styled.div`
  margin-bottom: 0.75rem;
`;

const SkeletonCTAButton = styled(SkeletonBase)`
  width: 120px;
  height: 38px;
  border-radius: 4px;
`;

export function SkeletonCTACard() {
  return (
    <SkeletonCTACardContainer>
      <SkeletonCTAContent>
        {/* Icon */}
        <SkeletonCTAIconArea>
          <SkeletonCircle size="32px" />
        </SkeletonCTAIconArea>

        {/* Title */}
        <SkeletonLine width="70%" height="18px" />

        {/* Description */}
        <SkeletonLine width="100%" height="14px" />
        <SkeletonLine width="90%" height="14px" />

        {/* Button */}
        <SkeletonCTAButton />
      </SkeletonCTAContent>
    </SkeletonCTACardContainer>
  );
}

/**
 * CTA Grid Skeleton (3 cards)
 */
const SkeletonCTAGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    gap: 1.5rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
`;

export function CTASkeleton() {
  return (
    <SkeletonCTAGrid>
      <SkeletonCTACard />
      <SkeletonCTACard />
      <SkeletonCTACard />
    </SkeletonCTAGrid>
  );
}
